import { BIOMES, type Contract, type ContractRejected, getItem } from '@shefira/shared';

import { npcName } from './quest-text.js';
import { uiFormat, uiText } from './text.js';

/**
 * The words of the contracts (ADR 0051): what each one asks, what a refusal
 * says, how long until the next ones. French with its accents; the DOM
 * strips them (`uiText`).
 */

export const CONTRACT_REFUSALS: Record<ContractRejected['reason'], string> = {
  invalid: 'Ce contrat n’existe pas.',
  'not-done': 'Ce contrat n’est pas encore rempli.',
  claimed: 'Tu as déjà touché ce contrat.',
  'bag-full': 'Ton sac est plein : fais-y de la place pour la récompense.',
  busy: 'Un instant…',
  'streak-bag-full':
    'Contrat touché, mais ton sac est trop plein pour la récompense de série : fais de la place, elle viendra au prochain contrat.',
};

/** What a contract asks, in a line. */
export function contractTitle(contract: Contract): string {
  const region = BIOMES[contract.biome].name;
  const count = contract.count;
  switch (contract.kind) {
    case 'kill-mob':
      return uiFormat('Chasse : {count} × {mob}', { count, mob: npcName(contract.mob ?? '') });
    case 'kill-biome':
      return uiFormat('Abats {count} créatures ({region})', { count, region });
    case 'camp':
      return uiFormat('Nettoie un camp ({region})', { region });
    case 'upgrade':
      return uiFormat('Tente {count} améliorations chez le forgeron', { count });
    case 'loot':
      return uiFormat('Ramasse {count} objets', { count });
    case 'cast':
      return uiFormat('Lance {count} sorts', { count });
    case 'bosses':
      return contract.count > 1
        ? uiFormat('Vaincs {count} boss de zone différents', { count })
        : uiText('Vaincs un boss de zone');
    case 'dailies':
      return uiFormat('Remplis {count} contrats du jour', { count });
    case 'events':
      return uiFormat('Gagne {count} événements mondiaux', { count });
  }
}

/** Under the title: where to go, or which bosses count. */
export function contractHint(contract: Contract): string | undefined {
  if (contract.kind === 'bosses') {
    return (contract.bosses ?? []).map((id) => npcName(id)).join(', ');
  }
  if (contract.kind === 'kill-mob') return BIOMES[contract.biome].name;
  return undefined;
}

/** A reward in words, for the chat: `55 or, Silex ×2`. */
export function contractRewardLine(reward: {
  gold: number;
  items: readonly { templateId: string; quantity: number }[];
}): string {
  const parts = reward.gold > 0 ? [uiFormat('{gold} or', { gold: reward.gold })] : [];
  for (const item of reward.items) {
    const name = getItem(item.templateId)?.name ?? item.templateId;
    parts.push(item.quantity > 1 ? `${name} ×${String(item.quantity)}` : name);
  }
  return parts.join(', ');
}

/** A countdown in hours and minutes: `5 h 12`, `12 min`, `2 j 4 h`. */
export function countdown(ms: number): string {
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days > 0) return uiFormat('{days} j {hours} h', { days, hours });
  if (hours > 0)
    return uiFormat('{hours} h {minutes}', { hours, minutes: String(rest).padStart(2, '0') });
  return uiFormat('{minutes} min', { minutes: rest });
}
